/**
 * Modal con el resumen de una venta del turno. Se abre desde la lista de
 * "Ventas del turno" en caja al hacer click en una fila, y muestra los
 * items vendidos (con miniatura del producto), totales y métodos de pago.
 *
 * Fetchea `orders.getById` cuando está abierto para evitar cargar detalles
 * de todos los pedidos del turno innecesariamente.
 */

import { Button } from '@atreus/ui';
import { Link } from '@tanstack/react-router';
import { Package, X } from 'lucide-react';
import { useEffect, type ReactElement } from 'react';

import { formatCOP } from '../../lib/format.js';
import { orderStatusChipClass, orderStatusLabels } from '../../lib/orders.js';
import { paymentMethodLabels } from '../../lib/payments.js';
import { trpc } from '../../lib/trpc.js';

import type { OrderOrigin, OrderStatus } from '@atreus/core';

const originLabels: Record<OrderOrigin, string> = {
  table: 'Mesa',
  delivery: 'Domicilio',
  counter: 'Mostrador',
};

export interface SaleDetailSeed {
  id: string;
  origin: OrderOrigin;
  tableLabel: string | null;
  tableNumber: number | null;
  status: OrderStatus;
  totalCents: number;
  closedAt: number | null;
  openedAt: number;
  payments: { method: string; amountCents: number }[];
}

export const SaleDetailModal = ({
  open,
  sale,
  onClose,
}: {
  open: boolean;
  sale: SaleDetailSeed | null;
  onClose: () => void;
}): ReactElement | null => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // La query se habilita sólo cuando hay venta y modal abierto — evita
  // fetches especulativos por cada fila de la lista.
  const detail = trpc.orders.getById.useQuery(
    { id: sale?.id ?? '' },
    { enabled: open && !!sale },
  );

  if (!open || !sale) return null;

  const when = sale.closedAt ?? sale.openedAt;
  const tableLabel =
    sale.tableLabel ??
    (sale.tableNumber != null ? String(sale.tableNumber) : null);

  const order = detail.data?.order;
  const items = detail.data?.items ?? [];

  // Derivados para desglose: si el backend entrega subtotal/tax/tip/discount
  // los usamos tal cual; si todavía está cargando mostramos solo el total
  // que viene de `sale`.
  const subtotal = order?.subtotalCents ?? null;
  const discount = order?.discountCents ?? 0;
  const tip = order?.tipCents ?? 0;
  const total = order?.totalCents ?? sale.totalCents;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-fg/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="atreus-sale-detail-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-border bg-surface-raised shadow-xl">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-border px-5 py-3">
          <div className="min-w-0">
            <h2
              id="atreus-sale-detail-title"
              className="flex flex-wrap items-center gap-2 text-lg font-black tracking-tight"
            >
              <span>Venta</span>
              <span className="font-mono text-fg-muted">
                #{sale.id.slice(-6).toUpperCase()}
              </span>
              <span
                className={
                  'rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ' +
                  orderStatusChipClass(sale.status)
                }
              >
                {orderStatusLabels[sale.status]}
              </span>
            </h2>
            <p className="mt-1 text-sm text-fg-muted">
              {originLabels[sale.origin]}
              {tableLabel ? ` · Mesa ${tableLabel}` : ''}
              {' · '}
              {new Date(when).toLocaleString('es-CO', {
                dateStyle: 'short',
                timeStyle: 'short',
              })}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            aria-label="Cerrar"
          >
            <X className="size-4" />
          </Button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto p-5">
          <h3 className="mb-3 text-xs font-bold uppercase tracking-wider text-fg-muted">
            Productos
          </h3>
          {detail.isLoading ? (
            <p className="text-sm text-fg-muted">Cargando…</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-fg-muted">Este pedido no tiene items.</p>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((item) => {
                const lineTotal = item.qty * item.unitPriceCents;
                return (
                  <li key={item.id} className="flex items-start gap-3 py-3">
                    <ProductThumb
                      imageUrl={item.productImageUrl ?? null}
                      name={item.productName ?? ''}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold">
                        {item.productName ?? 'Producto'}
                      </div>
                      {item.productSku ? (
                        <div className="font-mono text-xs text-fg-subtle">
                          {item.productSku}
                        </div>
                      ) : null}
                      {item.note ? (
                        <div className="mt-1 text-xs italic text-fg-muted">
                          {item.note}
                        </div>
                      ) : null}
                      <div className="mt-1 text-xs tabular-nums text-fg-muted">
                        {item.qty} × {formatCOP(item.unitPriceCents)}
                      </div>
                    </div>
                    <div className="shrink-0 text-base font-bold tabular-nums">
                      {formatCOP(lineTotal)}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Totales + pagos */}
        <div className="border-t border-border bg-surface px-5 py-4">
          <dl className="space-y-1 text-sm">
            {subtotal != null ? (
              <Row label="Total productos" value={formatCOP(subtotal)} />
            ) : null}
            {discount > 0 ? (
              <Row label="Descuento" value={`- ${formatCOP(discount)}`} />
            ) : null}
            {tip > 0 ? <Row label="Propina" value={formatCOP(tip)} /> : null}
            <div className="mt-2 border-t border-border pt-2">
              <Row label="Total" value={formatCOP(total)} strong />
            </div>
          </dl>

          <div className="mt-4">
            <h3 className="mb-2 text-xs font-bold uppercase tracking-wider text-fg-muted">
              Medios de pago
            </h3>
            {sale.payments.length === 0 ? (
              <p className="text-xs text-fg-muted">Sin pagos registrados.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {sale.payments.map((p, i) => (
                  <span
                    key={i}
                    className="rounded-full border border-border bg-surface-muted px-3 py-1 text-xs font-semibold tabular-nums"
                  >
                    {paymentMethodLabels[p.method] ?? p.method} ·{' '}
                    {formatCOP(p.amountCents)}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer con CTA para ir al pedido completo */}
        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <Button variant="ghost" size="md" onClick={onClose}>
            Cerrar
          </Button>
          <Button asChild size="md">
            <Link to="/orders/$orderId" params={{ orderId: sale.id }}>
              Ver pedido completo
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

const Row = ({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) => (
  <div className="flex items-center justify-between">
    <dt className={strong ? 'text-base font-bold' : 'text-fg-muted'}>
      {label}
    </dt>
    <dd className={strong ? 'text-lg font-black tabular-nums' : 'tabular-nums'}>
      {value}
    </dd>
  </div>
);

const ProductThumb = ({
  imageUrl,
  name,
}: {
  imageUrl: string | null;
  name: string;
}) => {
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={name}
        className="size-14 shrink-0 rounded-md border border-border object-cover"
      />
    );
  }
  return (
    <div
      className="flex size-14 shrink-0 items-center justify-center rounded-md border border-border bg-surface-muted text-fg-muted"
      aria-hidden
    >
      <Package className="size-5" />
    </div>
  );
};
